<template>
  <div class="job-list-container">
    <FilterLayout
      ref="filterList"
      class="filter-layout"
      hiddenKeywordSearch
      hiddenCandidateIdSearch
      hiddenCandidateNameSearch
      hiddenMyCandidateSwitch
      hiddenEmployeeIdSearch
      :searchTemplate.sync="searchTemplate"
      :searchTemplateList="searchTemplateList"
      :filterGroupList="filterList"
      :listLoading="loading"
      :candidateTotalCount="0"
      @search="handelSearch"
      @createSearchTemplate="handleCreateSearchTemplate"
      @updateSearchTemplate="handleUpdateSearchTemplate"
      @clearSearchTemplate="handleClearSearchTemplate"
      @deleteSearchTemplate="handleDeleteSearchTemplate"
    >
      <template #selectCountContainer>
        <div class="left-container">
          <el-checkbox class="select-all" :indeterminate="isIndeterminate" v-model="checkAllStatus" @change="handelSelectAll">{{ $t(`${i18nPrefix}.label.all`) }}</el-checkbox>
          <div class="job-count-container">
            <span class="job-count total-count">
              {{ `${$t(`${i18nPrefix}.label.totalCount`)} ${formatBigNumber(pageSetting.total)}` }}
            </span>
            <span class="job-count selected-count">
              {{ `${$t(`${i18nPrefix}.label.selected`)} ${formatBigNumber(selectedCount)}` }}
            </span>
          </div>
        </div>
      </template>
      <template #headBtnContainer>
        <div class="right-container">
          <div class="date-order-container" @click="handelDateClick">
            <span class="date-order-label">{{ $t(`${i18nPrefix}.label.date`) }}</span>
            <div class="date-order-icon">
              <i class="el-icon-caret-top" :class="dateOrder === 'up' && 'select'"/>
              <i class="el-icon-caret-bottom" :class="dateOrder === 'down' && 'select'"/>
            </div>
          </div>
          <el-dropdown trigger="click" @command="handelActionClick">
            <el-button class="dropdown-btn">
              <span class="label">{{ $t('common.label.operate') }}</span>
              <span class="more iconfont icona-xingzhuangbeifen8"></span>
            </el-button>
            <el-dropdown-menu class="job-list-dropdown-container" slot="dropdown">
              <el-dropdown-item v-if="hasJobEditPermission" command="refresh">{{ $t(`${i18nPrefix}.label.refresh`) }}</el-dropdown-item>
              <el-dropdown-item v-if="hasAgencyManagementPermission" command="postToAgency">{{ $t(`${i18nPrefix}.label.postToAgency`) }}</el-dropdown-item>
              <el-dropdown-item command="companyShare">{{ $t(`${i18nPrefix}.label.generateSiteShare`) }}</el-dropdown-item>
              <el-dropdown-item command="mobileShare">{{ $t(`${i18nPrefix}.label.generateMobileShare`) }}</el-dropdown-item>
              <el-dropdown-item v-if="hasJobClosePermission" command="close">{{ $t(`${i18nPrefix}.label.closeJob`) }}</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <template v-if="hasJobCreatePermission">
            <template v-if="canAddJob">
              <el-tooltip
                effect="light"
                popper-class="light-tooltip-popper"
                :content="$t(`${i18nPrefix}.tips.addJob`)"
              >
                <span class="iconfont iconAdd" @click="handelAddJobBtn"></span>
              </el-tooltip>
            </template>
            <template v-else>
              <el-tooltip
                effect="light"
                popper-class="light-tooltip-popper add-btn-tooltip"
                :content="$t(`${i18nPrefix}.message.jobCountNoEnough`)"
              >
                <span class="disabled iconfont iconAdd"></span>
              </el-tooltip>
            </template>
          </template>
        </div>
      </template>
      <template #listContainer>
        <div class="list-body">
          <div class="job-list-box" v-infinite-scroll="loadJobs" :infinite-scroll-immediate="false" :infinite-scroll-disabled="pageSetting.disabledLoad">
            <JobItem
              class="job-item"
              v-for="(item, index) in jobs"
              :key="index"
              :jobObj="item"
              :statisticChannelInfo="statisticChannelInfo"
              :jobFilterItemBindingList="jobFilterItemBindingList"
              @checkedJob="handelCheckedJob"
              @refreshJobInfo="handelRefreshJobInfo"
              @refreshJobList="handelRefreshJobList"
            />
          </div>
          <div class="no-data-tips" v-show="!loading && jobs.length === 0">{{ $t('el.table.emptyText') }}</div>
        </div>
      </template>
    </FilterLayout>
    <DialogWrapper
      class="share-dialog"
      :show.sync="shareDialog.show"
      :close-on-click-modal="false"
      :hideFooter="true"
    >
      <ShareDialog v-if="shareDialog.show" :companyId="selectCompany.id" :companyName="isEn ? selectCompany.name : selectCompany.cn_name" :shareUrl="shareDialog.url" @close="handelCloseShareDialog"/>
    </DialogWrapper>
    <AgencyDialog
      v-if="agencyDialog.show"
      :show.sync="agencyDialog.show"
      :jobId="agencyDialog.jobId"
      :isAll="agencyDialog.isAll"
      :jobIds="checkedJobIds"
      :filter="agencyDialog.filter"
    />
    <ConfirmDialog
      v-if="confirmDialog.show"
      :content="confirmDialog.content"
      :show.sync="confirmDialog.show"
      :title="confirmDialog.title"
      :loading="confirmDialog.loading"
      @confirm="confirmDialog.confirmMethod"
      @cancel="confirmDialog.cancelMethod"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { formatBigNumber } from '@/components/filters'
import mixin from './components/mixin'
import FilterLayout from '../applicantList/filterList/layout.vue'
import JobItem from './components/jobListItem.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import ConfirmDialog from '@/components/dialog/confirmDialog.vue'
import ShareDialog from './components/shareJobDialog.vue'
import AgencyDialog from './components/inviteAgencyDialog.vue'
import { addI18nProperty, createI18nProperty } from '@/i18n'
import { MUNICIPALITY_CITY, MUNICIPALITY_PROVINCE } from '@/constant/application'
import { JOB_STATUS } from '@/constant/job'
import CompanySelect from './components/companySelect.vue'
import { SHOW_HR_JOB_FILTER_OPTIONS } from '@/constant/common'
import { ROOT_COMPANY_ID } from '@/constant/company'

const JOB_LIST_FILTER_GROUP_INDEX = 0
const DEFAULT_CHANNEL_KEY = 'ajinga'
const CHANNEL_KEY_WO_TUI = 'wotui'

export default {
  mixins: [mixin],
  components: {
    JobItem,
    FilterLayout,
    DialogWrapper,
    ShareDialog,
    ConfirmDialog,
    AgencyDialog
  },
  data () {
    return {
      i18nPrefix: 'admin.job.list',
      formatBigNumber,
      checkAllStatus: false,
      isIndeterminate: false,
      loading: false,
      checkedJobIds: [],
      dateOrder: 'down',
      pageSetting: {
        pageSize: 10,
        page: 1,
        total: 0,
        disabledLoad: true
      },
      jobList: [],
      companyList: [],
      candidateStatusGroup: [],
      currentCompany: {},
      selectCompany: {},
      shareDialog: {
        show: false,
        url: ''
      },
      agencyDialog: {
        show: false,
        jobId: '',
        isAll: false,
        filter: {}
      },
      confirmDialog: {
        show: false,
        title: '',
        content: '',
        confirmMethod: null,
        cancelMethod: null,
        loading: false
      },
      canAddJob: true,
      addCompanyId: null,
      searchTemplate: null,
      searchTemplateList: [],
      isSavingSearchTemplate: false,
      location: {
        data: null,
        loading: false,
        loader: null,
        currentData: null,
        hasLoad: false,
        page: 1,
        pageSize: 10
      },
      totalParamsList: ['company_id', 'keyword', 'creator', 'location', 'job_type', 'is_include_sub_company'],
      multipleParamsList: ['creator', 'location'],
      statisticChannelInfo: null,
      search: {},
      searchParams: {
        keyword: '',
        company_id: '',
        creator: '',
        location: '',
        job_type: '0',
        is_include_sub_company: true,
        order: 'up'
      },
      jobFilterItemBindingList: [],
      channelListLoader: null,
      channelList: []
    }
  },
  computed: {
    jobs () {
      const jobList = []
      const checkedJobIds = this.checkedJobIds
      _.forEach(this.jobList, (item) => {
        this.$set(item, 'checked', false)
        if (this.checkAllStatus || checkedJobIds.includes(item.id)) {
          item.checked = true
          if (this.checkAllStatus && !checkedJobIds.includes(item.id)) {
            checkedJobIds.push(item.id)
          }
        }
        this.initCandidateStatusCount(item)
        jobList.push(item)
      })
      return this.jobList
    },
    selectedCount () {
      if (this.checkAllStatus) {
        return this.pageSetting.total
      }
      return this.checkedJobIds.length
    },
    isEn () {
      return this.$i18n.locale === 'en'
    },
    filterList () {
      const vm = this
      const list = [
        {
          title: this.$t(`${this.i18nPrefix}.label.jobInfo`),
          filterList: {
            keyword: {
              elType: 'el-input',
              label: this.$t(`${this.i18nPrefix}.label.jobTitle`),
              setParam: (params, val) => {
                params.keyword = val
              }
            },
            recruiter: {
              elType: 'el-select',
              label: this.$t(`${this.i18nPrefix}.label.recruiter`),
              optionLabel: 'name',
              optionValue: 'id',
              optionSearch: true,
              multiple: true,
              optionGroup: {},
              loader: this.loadCompanyRecruiterList,
              setParam: (params, val) => {
                params.creator = _.map(val, 'id').join(',')
              }
            },
            company: {
              elType: CompanySelect,
              label: null,
              multiple: true,
              props: {
                companyList: this.companyList
              },
              valueDisplay: {
                formatter: (value, filter, filterName, group, groupKey) => {
                  if (!value?.selected || value.selected.length < 1) {
                    return
                  }
                  const companyIds = _.map(value.selected, 'id')
                  return _.map(_.filter(this.companyList, item => _.includes(companyIds, item.id)), (item, index) => {
                    return {
                      valueKey: index,
                      value: item,
                      detail: `${group.title} - ${this.$t(`${this.i18nPrefix}.label.company`)}: ${item.i18nName}`,
                      summary: `${item.i18nName}`
                    }
                  })
                }
              },
              cancelSelected: (filterValue, value) => {
                filterValue.selected = _.filter(filterValue.selected, item => item.id !== value.id)
                return filterValue
              },
              setParam: (params, val) => {
                if (val) {
                  params.company_id = _.map(val.selected, 'id').join(',')
                  params.is_include_sub_company = val.includeSubCompany ? '1' : '0'
                }
              }
            },
            location: {
              elType: 'el-select',
              label: this.$t(`${this.i18nPrefix}.label.jobLocation`),
              optionLabel: 'locationName',
              optionValue: 'id',
              multiple: true,
              optionSearch: this.loadLocationList,
              loadTemplate: this.loadLocationTemplate,
              loader: this.getLocationList,
              loadMore: (filter) => {
                const location = vm.location
                if (location.currentData && location.page * location.pageSize > location.currentData.length) {
                  return
                }
                const data = location.currentData.slice(location.page * location.pageSize, (location.page + 1) * location.pageSize)
                location.page += 1
                filter.option.data.push(...data)
              },
              props: {
                popperClass: 'location-dropdown',
                noDataText: this.$t('admin.application.applicantList.label.pleaseStartTyping')
              },
              events: {
                'visible-change': (visible, filterName, filter) => {
                  if (visible) {
                    if (filter) {
                      filter.option.data = []
                    }
                  }
                }
              },
              setParam: (params, val) => {
                const group = _.groupBy(val, 'type_code')
                // 处理直辖市
                const municipalityCityIds = []
                _.forEach(group.district, location => {
                  const cityName = _.findKey(MUNICIPALITY_PROVINCE, city => city === location.id)
                  if (cityName) {
                    municipalityCityIds.push(MUNICIPALITY_CITY[cityName])
                  }
                })

                const countryIds = _.map(group.country, 'id')
                const provinceIds = _.map(group.district, 'id')
                const cityIds = [..._.map(group.city, 'id'), ...municipalityCityIds]
                params.location = _.concat(countryIds, provinceIds, cityIds).join(',')
              }
            },
            status: {
              elType: 'el-select',
              label: this.$t(`${this.i18nPrefix}.label.jobStatus.label`),
              optionValue: 'value',
              optionList: _.map(JOB_STATUS, (val, key) => createI18nProperty({ value: val }, 'label', `${this.i18nPrefix}.label.jobStatus.${key}`)),
              setParam: (params, val) => {
                params.job_type = val?.value
              }
            },
            channel: {
              elType: 'el-select',
              label: this.$t(`${this.i18nPrefix}.label.postChannel`),
              optionLabel: 'i18nName',
              optionValue: 'id',
              optionSearch: true,
              multiple: true,
              loader: this.loadPostingChannelList,
              setParam: (params, val) => {
                params.channel_id = _.map(val, 'id').join(',')
              }
            }
          }
        }
      ]
      return list
    }
  },
  async created () {
    this.loading = true
    await this.initCompanyList()
    await this.initJobListFilter()
    this.loadSearchTemplateList()
    this.getJobFilterItemBindingList()
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('company', ['getCompanyJobList', 'getCompanyChildrenTree', 'refreshJob', 'setJobStatus', 'getTeamMemberList', 'getJobFilterItemBinding', 'getCompanyJobPostingChannelList']),
    ...mapActions('worldLocation', ['getWorldLocationList']),
    ...mapActions('account', ['getAccountPageConfigList', 'createAccountPageConfig', 'updateAccountPageConfig', 'deleteAccountPageConfig']),
    async initCompanyList () {
      const companyTreeResp = await this.getCompanyChildrenTree()
      const currentCompany = {
        id: companyTreeResp.self.id,
        name: companyTreeResp.self.name,
        cn_name: companyTreeResp.self.cn_name,
        level: companyTreeResp.self.level
      }
      this.currentCompany = currentCompany
      const companyList = []
      this.companyList = this.formatCompanyData(companyTreeResp, currentCompany, companyList)
    },
    async initCompanyJobList () {
      this.jobList = await this.loadCompanyJobList(1)
      this.pageSetting.disabledLoad = false
    },
    async initJobListFilter () {
      const currentAccount = this.getCurrentAccount()
      const query = this.$route.query
      const params = this.searchParams
      params.job_type = '2'
      if (currentAccount && currentAccount.id) {
        params.creator = [currentAccount.id]
      }
      const totalParamsList = this.totalParamsList
      const multipleParamsList = this.multipleParamsList

      if (!_.has(query, 'is_include_sub_company')) {
        query.is_include_sub_company = '1'
      }

      if (_.has(query, 'channel_id')) {
        params.channel_id = _.chain(query.channel_id).split(',').map(channelId => parseInt(channelId)).filter().value()
      } else if (currentAccount?.root_company_id === ROOT_COMPANY_ID.COACH) {
        await this.loadPostingChannelList()
        params.channel_id = _.chain(this.channelList).filter(channel => channel.key !== CHANNEL_KEY_WO_TUI).map(channel => channel.id).value()
      }

      _.forEach(query, (value, key) => {
        if (value && multipleParamsList.includes(key)) {
          params[key] = _.map(value.split(','), (item) => _.toInteger(item))
        } else if (totalParamsList.includes(key)) {
          params[key] = value
        }
      })
      this.$nextTick(() => {
        const selectCompany = _.filter(this.companyList, item => item.id === _.parseInt(params.company_id))
        const search = this.$refs.filterList.search
        search[JOB_LIST_FILTER_GROUP_INDEX].company = {
          selected: selectCompany,
          includeSubCompany: params.is_include_sub_company === '1'
        }
        search[JOB_LIST_FILTER_GROUP_INDEX].keyword = params.keyword
        search[JOB_LIST_FILTER_GROUP_INDEX].location = params.location
        search[JOB_LIST_FILTER_GROUP_INDEX].status = params.job_type
        search[JOB_LIST_FILTER_GROUP_INDEX].recruiter = params.creator
        search[JOB_LIST_FILTER_GROUP_INDEX].channel = params.channel_id
        this.$refs.filterList.resetSearch({ search: search })
      })
    },
    async loadCompanyJobList (currentPage) {
      this.loading = true
      let jobList = []
      const jobFilterParams = this.searchParams
      try {
        const params = { page: currentPage, ...jobFilterParams }
        const resp = await this.getCompanyJobList(params)
        if (resp && resp.job_list) {
          jobList = resp.job_list
          this.pageSetting.total = resp.total
          this.canAddJob = resp.can_add_job
          this.addCompanyId = resp.add_company_id
          this.selectCompany = resp.company_info
          this.statisticChannelInfo = resp.statistic_channel_info
          if (!this.candidateStatusGroup || this.candidateStatusGroup.length === 0) {
            this.candidateStatusGroup = resp.group_info_list
          }
        }
      } finally {
        this.loading = false
      }
      return jobList
    },
    async loadPostingChannelList () {
      if (!this.channelListLoader) {
        this.channelListLoader = this.getCompanyJobPostingChannelList().then(resp => {
          if (resp) {
            this.channelList = _.map(resp.post_channels, channel => {
              if (channel.key === DEFAULT_CHANNEL_KEY) {
                return createI18nProperty(channel, 'i18nName', `${this.i18nPrefix}.label.defaultChannelName`)
              }
              return addI18nProperty(channel, 'i18nName', 'cn_name', 'name')
            })
          }
        })
      }
      await this.channelListLoader
      return this.channelList
    },
    async getJobFilterItemBindingList () {
      const companyId = this.currentCompany.id
      if (companyId in SHOW_HR_JOB_FILTER_OPTIONS) {
        const params = {
          company_id: companyId
        }
        const resp = await this.getJobFilterItemBinding({
          filterId: SHOW_HR_JOB_FILTER_OPTIONS[companyId],
          params
        })
        if (resp) {
          this.jobFilterItemBindingList = resp.filter_item_list
        }
      }
    },
    async loadJobs () {
      this.pageSetting.disabledLoad = true
      this.pageSetting.page++
      this.jobList.push(...await this.loadCompanyJobList(this.pageSetting.page))
      if (this.pageSetting.total > this.pageSetting.page * this.pageSetting.pageSize) {
        this.pageSetting.disabledLoad = false
      }
    },
    formatCompanyData (data, company, companyList) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      companyList.push(company)
      const allChildren = data.children
      const children = allChildren[company.id]
      if (children && children.length > 0) {
        for (const child of children) {
          this.formatCompanyData(data, child, companyList)
        }
      }
      return companyList
    },
    handelCheckedJob (jobObj) {
      if (!jobObj.checked) {
        if (!this.checkedJobIds.includes(jobObj.id)) {
          this.checkedJobIds.push(jobObj.id)
        }
        jobObj.checked = true
        this.checkAllStatus = this.checkedJobIds.length === this.jobs.length
        this.isIndeterminate = !this.checkAllStatus
      } else {
        this.checkedJobIds.splice(this.checkedJobIds.indexOf(jobObj.id), 1)
        jobObj.checked = false
        this.checkAllStatus = false
        this.isIndeterminate = this.checkedJobIds.length > 0
      }
    },
    handelSelectAll (val) {
      const jobList = this.jobs
      this.isIndeterminate = false
      _.forEach(jobList, (item) => {
        item.checked = val
        if (val && !this.checkedJobIds.includes(item.id)) {
          this.checkedJobIds.push(item.id)
        } else if (!val) {
          this.checkedJobIds.splice(this.checkedJobIds.indexOf(item.id), 1)
        }
      })
    },
    initCandidateStatusCount (jobObj) {
      const statusGroup = this.candidateStatusGroup
      const applicantInfo = jobObj.applicant_info
      const statusCountGroup = []
      _.forEach(statusGroup, (group) => {
        const statusList = []
        let groupCount = 0
        _.forEach(group.status_list, (status) => {
          const statusCount = applicantInfo[status.key] || 0
          groupCount += statusCount
          statusList.push({ ...status, count: statusCount, label: this.isEn ? status.name_en : status.name_cn })
        })
        statusCountGroup.push({ name: group.name, statusList: statusList, count: groupCount })
      })
      jobObj.statusCountGroup = statusCountGroup
    },
    handelAddJobBtn () {
      let path = '/v-admin/job/add'
      if (this.addCompanyId) {
        path += `?company_id=${this.addCompanyId}&back=${encodeURIComponent(this.$route.fullPath)}`
      }
      this.$router.push(path)
    },
    async handelChangeFilter () {
      this.pageSetting.disabledLoad = true
      this.checkAllStatus = false
      this.checkedJobIds = []
      this.jobList = []
      this.pageSetting.page = 1
      this.pageSetting.total = 0
      this.jobList = await this.loadCompanyJobList(this.pageSetting.page)
      if (this.pageSetting.total > this.pageSetting.page * this.pageSetting.pageSize) {
        this.pageSetting.disabledLoad = false
      }
    },
    handelDateClick () {
      this.dateOrder = this.dateOrder === 'up' ? 'down' : 'up'
      this.searchParams.order = this.dateOrder
      this.handelChangeFilter()
    },
    handelActionClick (command) {
      if (command === 'refresh') {
        this.showConfirmRefreshDialog()
      } else if (command === 'postToAgency') {
        this.handelPostToAgency()
      } else if (command === 'companyShare') {
        this.handelCompanyShare()
      } else if (command === 'mobileShare') {
        this.handelMobileShare()
      } else if (command === 'close') {
        this.showConfirmCloseDialog()
      }
    },
    showConfirmRefreshDialog () {
      if (this.checkedJobIds.length === 0) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.selectAtLeastJob`))
        return
      }
      this.confirmDialog.title = this.$t(`${this.i18nPrefix}.label.refresh`)
      this.confirmDialog.content = this.$t(`${this.i18nPrefix}.message.refreshJobTip`, { count: formatBigNumber(this.selectedCount) })
      this.confirmDialog.confirmMethod = this.handelRefreshJobs
      this.confirmDialog.cancelMethod = this.handelCancelRefreshJobs
      this.confirmDialog.show = true
    },
    async handelRefreshJobs () {
      this.confirmDialog.loading = true
      try {
        const params = {}
        if (this.checkAllStatus) {
          params.filter = this.searchParams
          params.select_all = true
        } else {
          params.job_ids = this.checkedJobIds.join(',')
        }
        const resp = await this.refreshJob(params)
        if (resp === 'success') {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.refreshSuccess`))
          this.checkAllStatus = false
          this.checkedJobIds = []
          this.confirmDialog.show = false
        } else {
          this.$message.error(this.$t('error.message.serverError'))
        }
      } finally {
        this.confirmDialog.loading = false
      }
    },
    handelCancelRefreshJobs () {
      this.$message.info(this.$t(`${this.i18nPrefix}.message.cancelAction`))
    },
    handelCompanyShare () {
      this.shareDialog.url = `${location.origin}/company-detail-new/${this.selectCompany.id}/`
      this.shareDialog.show = true
    },
    handelMobileShare () {
      this.shareDialog.url = `${location.origin}/mobile-web-more-jobs/${this.selectCompany.id}/`
      this.shareDialog.show = true
    },
    handelCloseShareDialog () {
      this.shareDialog.url = ''
      this.shareDialog.show = false
    },
    handelPostToAgency () {
      if (this.checkedJobIds.length === 0) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.selectAtLeastJob`))
        return
      }
      this.agencyDialog.isAll = false
      if (this.checkAllStatus) {
        this.agencyDialog.isAll = true
        this.agencyDialog.filter = this.searchParams
      }
      this.agencyDialog.jobId = this.checkedJobIds[0]
      this.agencyDialog.show = true
    },
    showConfirmCloseDialog () {
      if (this.checkedJobIds.length === 0) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.selectAtLeastJob`))
        return
      }
      this.confirmDialog.title = this.$t(`${this.i18nPrefix}.label.closeJob`)
      this.confirmDialog.content = this.$t(`${this.i18nPrefix}.message.closeJobTip`, { count: formatBigNumber(this.selectedCount) })
      this.confirmDialog.confirmMethod = this.handelConfirmCloseJob
      this.confirmDialog.cancelMethod = this.handelCancelCloseJob
      this.confirmDialog.show = true
    },
    async handelConfirmCloseJob () {
      this.confirmDialog.loading = true
      try {
        const params = { action: 'close' }
        if (this.checkAllStatus) {
          params.filter = this.searchParams
          params.select_all = true
        } else {
          params.job_ids = this.checkedJobIds.join(',')
        }
        const resp = await this.setJobStatus(params)
        if (resp && resp.status === 'success') {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.closingSuccess`))
          this.handelChangeFilter()
        } else {
          this.$message.error(this.$t('error.message.serverError'))
          this.handelChangeFilter()
        }
        this.confirmDialog.show = false
      } finally {
        this.confirmDialog.loading = false
      }
    },
    handelCancelCloseJob () {
      this.$message.info(this.$t(`${this.i18nPrefix}.message.cancelAction`))
    },
    async loadLocationList (keywords, loadData, valueList) {
      if (!_.trim(keywords) && !valueList?.length > 0) {
        return []
      }
      if (this.location.loader) {
        await this.location.loader
      }
      const filteredList = _.filter(this.location.data, location => {
        if (valueList?.length > 0) {
          return _.includes(valueList, location.id)
        } else {
          const keywordsList = _.map(location.searchTextList, item => _.toUpper(item))
          return _.some(keywordsList, item => _.includes(item, _.toUpper(keywords)))
        }
      })
      this.location.page = 1
      this.location.currentData = filteredList
      return valueList ? filteredList : filteredList.slice(0, this.location.pageSize)
    },
    async loadLocationTemplate (valueList, filter, formattedFilterValues) {
      if (valueList?.length < 1) {
        return []
      }
      const locationList = await this.loadLocationList(null, null, valueList)
      if (locationList.length > 0) {
        filter.option.data = locationList
        formattedFilterValues.location = locationList
      }
    },
    async getLocationList () {
      if (this.location.hasLoad) {
        return
      }
      this.location.hasLoad = true
      this.location.loader = this.getWorldLocationList().then(resp => {
        if (!resp) {
          return
        }
        this.location.data = _.map(resp, location => {
          addI18nProperty(location, 'i18nName', 'cn_name', 'name')
          location.searchTextList = []
          location.searchTextList.push(location.name, location.cn_name, location.pinyin_name)
          if (location.parent_id) {
            const locationInfo = _.find(resp, item => item.id === location.parent_id)
            location.parent = locationInfo
            location.searchTextList.push(locationInfo.name, locationInfo.cn_name, locationInfo.pinyin_name)
          }
          // 处理四个直辖市, 不出现在下拉列表中
          if (_.findKey(MUNICIPALITY_CITY, city => city === location.id)) {
            location.searchTextList = null
          }
          if (location.type_code === 'region') {
            location.searchTextList = null
          }
          const nameList = []
          if (location.parent) {
            this.getParentLocation(location, nameList)
          }
          location.locationName = _.chain([location.i18nName, ...nameList]).filter().join(' - ').value()
          return location
        })
        this.location.loader = null
      })
    },
    getParentLocation (location, nameList) {
      const parent = location.parent
      if (parent) {
        if (parent.searchTextList) {
          nameList.push(parent.i18nName)
        }
        if (parent.parent_id) {
          this.getParentLocation(parent, nameList)
        }
      }
    },
    loadCompanyList () {
      addI18nProperty()
      const list = [...this.companyList]
      return list
    },
    async loadCompanyRecruiterList () {
      const params = { companyId: this.currentCompany.id, is_all: true }
      if (this.hasOnlySeeMyJobsPermission) {
        params.only_self = true
      }
      const resp = await this.getTeamMemberList(params)
      const hrList = _.orderBy(resp.results, hr => hr.name.toLowerCase(), 'asc')
      const disabledHrList = _.filter(hrList, hr => !hr.active)
      const jobMemberList = [
        createI18nProperty({
          children: _.filter(hrList, hr => hr.active)
        }, 'label', `${this.i18nPrefix}.label.recruiter`)
      ]
      if (disabledHrList.length > 0) {
        jobMemberList.push(createI18nProperty({ children: disabledHrList }, 'label', `${this.i18nPrefix}.label.disabledRecruiter`))
      }
      return jobMemberList
    },
    async handleCreateSearchTemplate (template, callback) {
      if (this.isSavingSearchTemplate) {
        return
      }
      this.isSavingSearchTemplate = true
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.createAccountPageConfig({
          page_type: 'JOB_LIST',
          config: {
            name: template.name,
            search: template.search
          }
        })
        if (resp) {
          const templateInfo = {
            _config: resp,
            id: resp.id,
            name: resp.config.name,
            search: resp.config.search
          }
          callback(templateInfo)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
        this.isSavingSearchTemplate = false
      }
    },
    async handleUpdateSearchTemplate (template, callback) {
      if (this.isSavingSearchTemplate) {
        return
      }
      this.isSavingSearchTemplate = true
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.updateAccountPageConfig({
          id: template._config.id,
          config: {
            name: template.name,
            search: template.search
          }
        })
        if (resp) {
          const templateInfo = {
            _config: resp,
            id: resp.id,
            name: resp.config.name,
            search: resp.config.search
          }
          callback(templateInfo)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
        this.isSavingSearchTemplate = false
      }
    },
    async handleClearSearchTemplate () {
      this.searchTemplate = null
    },
    async handleDeleteSearchTemplate (searchTemplate, deletedTemplateIndex) {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.deleteAccountPageConfig({
          id: searchTemplate.id
        })
        if (resp) {
          this.searchTemplateList.splice(deletedTemplateIndex, 1)
          this.$message({
            type: 'success',
            message: this.$t(`${this.i18nPrefix}.message.deleteSuccess`)
          })
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadSearchTemplateList () {
      const resp = await this.getAccountPageConfigList({ page_type: 'JOB_LIST' })
      if (resp) {
        this.searchTemplateList = _.map(resp, pageConfig => {
          return {
            _config: pageConfig,
            id: pageConfig.id,
            name: pageConfig.config.name,
            search: pageConfig.config.search
          }
        })
      }
    },
    parseSearchToParams (search) {
      if (!search) {
        search = this.search
      } else {
        this.search = search
      }
      const params = { order: this.dateOrder, page_size: this.pageSetting.pageSize }
      const filterList = _.filter(this.filterList, item => !item.hidden)
      _.forEach(this.search, (group, groupKey) => {
        // 不要使用FilterLayout组件提供的keywords，这个输入框在这个页面上是禁用的
        // if (groupKey === 'keywords') {
        //   params.keyword = group
        //   return
        // }
        const groupFilters = filterList[groupKey]
        _.forEach(group, (filterValue, filterName) => {
          const filter = groupFilters?.filterList[filterName]
          if (!filter || !filterValue) {
            return
          }
          filter.setParam(params, filterValue)
        })
      })
      return params
    },
    handelSearch (search, nextPage) {
      const params = this.parseSearchToParams(search)
      this.searchParams = params
      const paramsList = []
      _.forEach(this.searchParams, (value, key) => {
        if (_.isNumber(value)) {
          paramsList.push(`${key}=${value}`)
        } else if (_.isString(value)) {
          paramsList.push(`${key}=${value}`)
        }
      })
      const newParams = paramsList.join('&')
      if (newParams) {
        const path = `${this.$route.path}?${newParams}`
        if (path !== this.$route.fullPath) {
          this.$router.push(path)
        }
      }
      this.handelChangeFilter()
    },
    handelRefreshJobInfo (jobInfo) {
      if (!jobInfo) {
        return
      }
      const jobIndex = _.findIndex(this.jobList, ['id', jobInfo.id])
      this.jobList.splice(jobIndex, 1, jobInfo)
    },
    handelRefreshJobList () {
      if (this.searchParams.job_type !== '0') {
        this.handelChangeFilter()
      }
    }
  },
  mounted () {
  }
}
</script>

<style lang="scss" scoped>
.job-list-container {
  width: 100%;
  display: flex;
  position: relative;
  height: 100%;

  .filter-layout {
    width: 100%;
  }

  .filter-container {
    margin-left: -20px;
    flex-shrink: 0;
  }

  .job-filter-container {
    width: 252px;
    height: 100%;
    padding: 20px;
    border: 1px solid #eef2f4;
    border-radius: 6px;
    background: #F7F8FA;
    overflow: auto;

    .item-container {
      padding: 14px 0;
      border-bottom: 1px solid #E7EAEB;
      .label {
        font-size: 16px;
        font-weight: bold;
        color: #606266;
        margin-bottom: 10px;
        display: block;
      }
    }
  }

  .left-container {
    display: flex;
    align-items: baseline;
    font-size: 14px;
    margin-right: 10px;

    .select-all {
      line-height: 32px;
      margin-right: 4px;
    }

    .job-count-container {
      display: flex;

      .job-count {
        padding: 6px 20px;
        border: 1px solid #dddedf;
        color: #303133 ;
        border-radius: 4px 4px 0 0;
        line-height: 18px;
        margin-left: 10px;
        margin-bottom: 14px;
      }
    }

    .filter-keyword-container {
      > span {
        padding: 6px 20px;
        color: #303133 ;
        border-radius: 18px;
        line-height: 18px;
        margin-left: 10px;
        border: 1px solid #dddedf;

        .btn-icon {
          cursor: pointer;
          margin-left: 5px;
        }
      }
    }
  }

  .right-container {
    color: #606266;
    margin-left: 16px;
    margin-right: 0;

    .date-order-container {
      cursor: pointer;
      display: inline-flex;
      vertical-align: middle;
      align-items: center;
    }

    .date-order-icon {
      display: flex;
      flex-direction: column;
      align-items: center;
      height: 28px;
      width: 14px;
      font-size: 12px;
      margin-left: 6px;
      margin-right: 24px;
      vertical-align: middle;
      justify-content: center;
      overflow: initial;
      color: #909399;

      .el-icon-caret-top {
        margin-bottom: -3px;
      }

      .select {
        color: #0096CC;
      }

      .el-icon-caret-bottom {
        margin-top: -3px;
      }
    }

    .more {
      margin-left: 18px;
    }

    .dropdown-btn {
      padding: 6px 14px;
      display: inline-block;
      vertical-align: middle;
      border-color: #0096CC;
      color: #0096CC;

      .label {
        font-size: 14px;
        line-height: 20px;
        height: 20px;
      }
    }

    .iconAdd {
      display: inline-block;
      vertical-align: middle;
      font-size: 34px;
      cursor: pointer;
      color: #0096CC;
      height: unset;
      margin-left: 24px;
      line-height: unset;

      &.disabled {
        color: #ccc;
      }
    }
  }

  .list-body {
    margin-top: 14px;
    overflow: auto;
    height: calc(100% - 15px);
    position: relative;

    .job-list-box {
      min-width: 1000px;
    }

    .job-item {
      margin-bottom: 15px;
    }

    .loading {
      position: absolute;
    }

    .no-data-tips {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #909399;
      height: 100%;
    }
  }

  ::v-deep .el-checkbox {
    .el-checkbox__input {
      zoom: 120%;
    }
  }
}
</style>
<style lang="scss">
.job-list-dropdown-container {
  &.el-popper {
    .popper__arrow {
      display: none;
    }
  }
}

.el-message-box__wrapper {
  .close-job-confirm {
    overflow: unset;
    position: relative;
    border: 0;
    box-shadow: unset;

    &::before {
      content: '';
      width: 10px;
      height: 100%;
      background: #666666;
      position: absolute;
      top: 0;
      left: -6px;
    }
  }
}
.el-dropdown-menu {
  .el-dropdown-menu__item {
    &:not(.is-disabled) {
      &:hover {
        background: #F0F0F0;
        color: #606266;
      }
    }
  }
}
</style>
